import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { BriefcaseBusiness, Loader, AlertCircle, Sparkles, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODES = [{ v: 'online', l: 'Online' }, { v: 'in_person', l: 'In person' }, { v: 'hybrid', l: 'Hybrid' }];

const TutorSignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', subjects: '',
    qualifications: '', years_experience: '', availability: '',
    teaching_mode: 'online', short_bio: '', hourly_rate: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/signup/tutor`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subjects: form.subjects.trim(),
        qualifications: form.qualifications.trim() || null,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        availability: form.availability.trim() || null,
        teaching_mode: form.teaching_mode,
        short_bio: form.short_bio.trim() || null,
        hourly_rate: form.hourly_rate.trim() || null,
      });
      setSubmitted(true);
      toast.success('Application received — we will be in touch.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit application.');
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-6" data-testid="tutor-signup-success">
        <div className="max-w-lg bg-white rounded-2xl border border-gray-200 p-10 text-center shadow-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4"><Check className="h-6 w-6 text-emerald-700" /></div>
          <div className="text-2xl font-bold text-gray-900">Application received</div>
          <p className="text-sm text-gray-600 mt-3">Thanks {form.full_name.split(' ')[0]}, our team is reviewing your details. You'll get an email with a decision and — if approved — a temporary password and a link to the tutor portal.</p>
          <div className="mt-6 text-[11px] text-gray-500">Have a CV ready? Strengthen your application by also submitting the <Link className="underline text-red-700 font-bold" to="/become-tutor">full tutor application with CV upload</Link>.</div>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/" className="h-10 px-5 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider flex items-center">← Winhouse Home</Link>
            <Link to="/tutor/login" className="h-10 px-5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider flex items-center">Tutor Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#FAF9F6]" data-testid="tutor-signup-page">
      <div className="lg:w-5/12 bg-black text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        <div>
          <div className="text-[#D3D86F] text-xs uppercase tracking-[3px] font-bold mb-2">WINHOUSE TUTORS</div>
          <div className="text-3xl lg:text-4xl font-bold leading-tight">Apply to teach</div>
          <div className="text-sm text-gray-300 mt-3 max-w-md">Tell us about your teaching background. Our team reviews every request — once approved you'll receive a temporary password and tutor-portal access.</div>
          <ul className="mt-8 space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Get matched with serious students.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> AI-assisted lesson recaps.</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#D3D86F] mt-0.5" /> Transparent 70% commission split.</li>
          </ul>
          <Link to="/become-tutor" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[2px] text-[#D3D86F] underline" data-testid="tutor-signup-cv-link">
            <FileText className="h-3 w-3" /> Full application with CV upload
          </Link>
        </div>
        <div className="mt-10 lg:mt-0">
          <div className="flex items-center gap-2 text-[#D3D86F]"><Sparkles className="h-3 w-3" /><span className="text-[10px] uppercase tracking-[2px]">Approval typically within 2-3 business days</span></div>
        </div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-[#D3D86F]/10 blur-3xl" />
      </div>

      <div className="lg:w-7/12 flex items-center justify-center p-8 lg:p-14">
        <form onSubmit={submit} className="w-full max-w-xl space-y-4" data-testid="tutor-signup-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[2px] text-gray-500 font-bold">
            <BriefcaseBusiness className="h-4 w-4 text-red-700" /> Tutor portal request
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" v={form.full_name} on={(v) => u('full_name', v)} required testid="tutor-signup-name" />
            <Field label="Email" type="email" v={form.email} on={(v) => u('email', v)} required testid="tutor-signup-email" />
            <Field label="Phone" v={form.phone} on={(v) => u('phone', v)} required testid="tutor-signup-phone" />
            <Field label="Years of experience" type="number" v={form.years_experience} on={(v) => u('years_experience', v)} testid="tutor-signup-years" />
            <div className="sm:col-span-2"><Field label="Subjects you teach" v={form.subjects} on={(v) => u('subjects', v)} required testid="tutor-signup-subjects" hint="e.g. IELTS Writing, VCE English, GMAT Quant" /></div>
            <div className="sm:col-span-2"><Field label="Qualifications" v={form.qualifications} on={(v) => u('qualifications', v)} testid="tutor-signup-quals" hint="Degrees, certifications, exam scores" /></div>
            <div>
              <Label>Teaching mode</Label>
              <select value={form.teaching_mode} onChange={(e) => u('teaching_mode', e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="tutor-signup-mode">
                {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </div>
            <Field label="Hourly rate expectation (optional)" v={form.hourly_rate} on={(v) => u('hourly_rate', v)} testid="tutor-signup-rate" hint="e.g. $60 AUD" />
            <div className="sm:col-span-2"><Field label="Availability" v={form.availability} on={(v) => u('availability', v)} testid="tutor-signup-avail" hint="e.g. weekday evenings, weekends" /></div>
            <div className="sm:col-span-2">
              <Label>Short bio</Label>
              <textarea value={form.short_bio} onChange={(e) => u('short_bio', e.target.value)} rows={3} className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="tutor-signup-bio" placeholder="A 2-3 sentence intro that we can show students." />
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200" data-testid="tutor-signup-error">
              <AlertCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-900">{error}</div>
            </div>
          )}

          <button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50" data-testid="tutor-signup-submit">
            {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
          <div className="text-[11px] text-gray-500 text-center">
            Already a Winhouse tutor? <Link to="/tutor/login" className="underline text-gray-700">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 block">{children}</label>;
const Field = ({ label, v, on, type = 'text', required, testid, hint }) => (
  <div>
    <Label>{label}</Label>
    <input type={type} required={required} value={v} onChange={(e) => on(e.target.value)} className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={testid} />
    {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default TutorSignupPage;
